// Pure reconciliation helpers for the SiteCaptureScreen subscriber.
// Extracted from site-capture-screen.tsx so that file stays under the
// max-lines policy, mirroring room-capture-reconcile.ts. No DOM, no
// React — these are easy to unit-test against the CaptureItemRow shape.

import type { CaptureItemRow } from '@citycare/shared'

import type { AssetRowPatch } from '../components/asset-row'

import { preserveDisplayFields } from './room-capture-reconcile'

// Build the optimistic row that shadows `existing` while a tap is in
// flight: the authoritative quantity / zero-confirmed come from the
// patch, every other field is carried over unchanged.
export function applyOptimisticPatch(
  existing: CaptureItemRow,
  patch: AssetRowPatch,
): CaptureItemRow {
  return {
    ...existing,
    quantity: patch.quantity ?? existing.quantity,
    isZeroConfirmed: patch.isZeroConfirmed ?? existing.isZeroConfirmed,
  }
}

// Narrow a captureClient.dispatch result to its echoed authoritative
// CaptureItemRow, or null when the result is not a row-bearing echo
// (e.g. a coalesced no-op). Mirrors the inline guard the room-capture
// path applies before upserting the echo.
export function extractEchoedItem(result: unknown): CaptureItemRow | null {
  if (
    result !== null &&
    typeof result === 'object' &&
    'item' in result &&
    result.item !== null &&
    typeof result.item === 'object' &&
    'syncVersion' in result.item
  ) {
    return result.item as CaptureItemRow
  }
  return null
}

// Reconcile an echoed authoritative row into the optimistic-override map
// for `itemId`. Authoritative quantity / syncVersion / id win, but the
// reference/display fields the local PowerSync JOIN dropped (blank names,
// and the 'Count' unit_of_measure default the JOIN substitutes when the
// synced reference tables lag replication after a WiFi drop) fall back to
// the prior override row — the same guard the room-capture echo path
// applies via preserveDisplayFields. Without it, applying a quantity
// could blank a populated row or flip a measurement asset (m/m²/m³) to
// the integer Count keypad mid-capture. `fallbackPrior` covers the rare
// case where no override is recorded yet for `itemId`.
export function reconcileEchoOverride(
  overrides: ReadonlyMap<string, CaptureItemRow>,
  itemId: string,
  serverItem: CaptureItemRow,
  fallbackPrior: CaptureItemRow,
): Map<string, CaptureItemRow> {
  const prior = overrides.get(itemId) ?? fallbackPrior
  const next = new Map(overrides)
  next.set(itemId, preserveDisplayFields(prior, serverItem))
  return next
}
